// WP-0.2 (§18.1 amounts, normative everywhere): currency code prefix, never symbol-only —
// `PKR 1,25,000` (lakh/crore grouping per approved setting) · `USD 1,234.56`.
// Fraction is omitted when zero (§18.4 scripts: `PKR 800`, `USD 500` — never `.00`).
// Negative = leading `−` (U+2212). Green/danger colouring is the UI's job, not ours.
import { currencyInfo } from './currency';
import type { Money } from './money';

/** ST-1 number-formatting setting: standard 1,000,000 vs lakh/crore 10,00,000. */
export type GroupingMode = 'standard' | 'lakhCrore';

export interface FormatOptions {
  /** Defaults to lakh/crore — the §18.1 primary example; ST-1 overrides per owner setting. */
  readonly grouping?: GroupingMode;
}

function groupDigits(digits: string, mode: GroupingMode): string {
  if (digits.length <= 3) return digits;
  const head = digits.slice(0, -3);
  const tail = digits.slice(-3);
  const groups: string[] = [];
  const size = mode === 'standard' ? 3 : 2;
  for (let end = head.length; end > 0; end -= size) {
    groups.unshift(head.slice(Math.max(0, end - size), end));
  }
  return `${groups.join(',')},${tail}`;
}

export function formatMoney(m: Money, opts?: FormatOptions): string {
  const grouping = opts?.grouping ?? 'lakhCrore';
  const info = currencyInfo(m.currency);
  const abs = m.minorUnits < 0 ? BigInt(-m.minorUnits) : BigInt(m.minorUnits);
  const per = BigInt(info.minorPerMajor);
  const intDigits = (abs / per).toString();
  const fracMinor = abs % per;
  const frac = fracMinor === 0n ? '' : `.${fracMinor.toString().padStart(info.decimalDigits, '0')}`;
  const sign = m.minorUnits < 0 ? '−' : '';
  return `${sign}${m.currency} ${groupDigits(intDigits, grouping)}${frac}`;
}

/** FX-3: non-base values render as `≈ PKR … (indicative)` on a muted second line. */
export function formatIndicative(m: Money, opts?: FormatOptions): string {
  return `≈ ${formatMoney(m, opts)} (indicative)`;
}
